/**
 * Error Recovery Service
 *
 * Coordinates error recovery actions, retry logic, and provides
 * user-friendly error messages with actionable recovery steps.
 */

import {
  UpdaterError,
  ErrorCode,
  RecoveryAction,
  RecoveryInstruction,
  WizardStep,
} from '../types/wizard-types';
import { ILogger } from '../types/imx-types';

/**
 * Retry configuration for different error types
 */
export interface RetryConfig {
  maxAttempts: number;
  initialDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  backoffMultiplier: number;
}

/**
 * Recovery attempt result
 */
export interface RecoveryAttempt {
  success: boolean;
  attempts: number;
  error?: Error;
}

/**
 * Recovery action handler
 */
export type RecoveryHandler = () => Promise<void>;

/**
 * Error Recovery Coordinator
 *
 * Manages error recovery strategies, retry logic, and recovery action execution.
 * Tracks retry attempts and provides exponential backoff for transient failures.
 */
export class ErrorRecoveryService {
  // Default retry configurations by error code
  private readonly DEFAULT_RETRY_CONFIGS: Map<ErrorCode, RetryConfig> = new Map([
    // Network/communication errors - retry with backoff
    [
      ErrorCode.DEVICE_OPEN_FAILED,
      { maxAttempts: 3, initialDelay: 1000, maxDelay: 5000, backoffMultiplier: 2 },
    ],
    [
      ErrorCode.BOOTLOADER_TRANSFER_FAILED,
      { maxAttempts: 3, initialDelay: 2000, maxDelay: 10000, backoffMultiplier: 2 },
    ],
    [
      ErrorCode.EASY_INSTALLER_TRANSFER_FAILED,
      { maxAttempts: 3, initialDelay: 2000, maxDelay: 10000, backoffMultiplier: 2 },
    ],
    [
      ErrorCode.FIRMWARE_FLASH_FAILED,
      { maxAttempts: 2, initialDelay: 3000, maxDelay: 10000, backoffMultiplier: 2 },
    ],

    // Timeout errors - fewer retries with longer delays
    [
      ErrorCode.TIMEOUT,
      { maxAttempts: 2, initialDelay: 5000, maxDelay: 15000, backoffMultiplier: 2 },
    ],
    [
      ErrorCode.DEVICE_NOT_RE_ENUMERATED,
      { maxAttempts: 2, initialDelay: 10000, maxDelay: 30000, backoffMultiplier: 2 },
    ],

    // User errors - no automatic retry
    [
      ErrorCode.EID_INVALID_FORMAT,
      { maxAttempts: 0, initialDelay: 0, maxDelay: 0, backoffMultiplier: 1 },
    ],
    [
      ErrorCode.EID_INVALID_CHECKSUM,
      { maxAttempts: 0, initialDelay: 0, maxDelay: 0, backoffMultiplier: 1 },
    ],
    [
      ErrorCode.NO_DEVICE_SELECTED,
      { maxAttempts: 0, initialDelay: 0, maxDelay: 0, backoffMultiplier: 1 },
    ],
  ]);

  // Track retry attempts per error code
  private retryAttempts: Map<ErrorCode, number> = new Map();

  constructor(private logger: ILogger) {}

  // ============================================================================
  // Recovery Execution
  // ============================================================================

  /**
   * Execute a recovery action with retry logic
   */
  async executeRecovery(
    error: UpdaterError,
    handler: RecoveryHandler,
    customConfig?: Partial<RetryConfig>
  ): Promise<RecoveryAttempt> {
    const config = this.getRetryConfig(error.code, customConfig);

    this.logger.info('Starting recovery attempt', {
      errorCode: error.code,
      maxAttempts: config.maxAttempts,
    });

    // Reset attempt counter for this error
    this.retryAttempts.set(error.code, 0);

    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
      this.retryAttempts.set(error.code, attempt);

      try {
        this.logger.info(`Recovery attempt ${attempt}/${config.maxAttempts}`);

        // Execute recovery handler
        await handler();

        this.logger.success(`Recovery succeeded on attempt ${attempt}`);

        return {
          success: true,
          attempts: attempt,
        };
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));

        this.logger.warning(`Recovery attempt ${attempt} failed`, lastError);

        // If not last attempt, wait before retrying
        if (attempt < config.maxAttempts) {
          const delay = this.calculateBackoffDelay(
            attempt,
            config.initialDelay,
            config.maxDelay,
            config.backoffMultiplier
          );

          this.logger.info(`Waiting ${delay}ms before retry`);
          await this.sleep(delay);
        }
      }
    }

    // All attempts failed
    this.logger.error('All recovery attempts failed', {
      attempts: config.maxAttempts,
      lastError,
    });

    return {
      success: false,
      attempts: config.maxAttempts,
      error: lastError,
    };
  }

  /**
   * Check if automatic retry is recommended for an error
   */
  shouldAutoRetry(errorCode: ErrorCode): boolean {
    const config = this.getRetryConfig(errorCode);
    return config.maxAttempts > 0;
  }

  /**
   * Get number of retry attempts remaining for an error
   */
  getRemainingRetries(errorCode: ErrorCode): number {
    const config = this.getRetryConfig(errorCode);
    const attempts = this.retryAttempts.get(errorCode) || 0;
    return Math.max(0, config.maxAttempts - attempts);
  }

  /**
   * Reset retry counter for an error
   */
  resetRetryCounter(errorCode: ErrorCode): void {
    this.retryAttempts.delete(errorCode);
  }

  /**
   * Reset all retry counters
   */
  resetAllCounters(): void {
    this.retryAttempts.clear();
  }

  // ============================================================================
  // Error Analysis
  // ============================================================================

  /**
   * Get user-friendly error message
   */
  getUserMessage(error: UpdaterError): string {
    return error.message;
  }

  /**
   * Get recovery instructions sorted by priority
   */
  getRecoveryInstructions(error: UpdaterError): RecoveryInstruction[] {
    return [...error.recoveryActions].sort((a, b) => a.order - b.order);
  }

  /**
   * Get primary recovery action (highest priority)
   */
  getPrimaryRecoveryAction(error: UpdaterError): RecoveryInstruction | null {
    const instructions = this.getRecoveryInstructions(error);
    return instructions.length > 0 ? instructions[0] : null;
  }

  /**
   * Check if error is recoverable
   */
  isRecoverable(error: UpdaterError): boolean {
    return error.recoveryActions.length > 0;
  }

  /**
   * Check if error requires manual intervention
   */
  requiresManualIntervention(error: UpdaterError): boolean {
    const primaryAction = this.getPrimaryRecoveryAction(error);

    if (!primaryAction) return true;

    // These actions require user intervention
    const manualActions = [
      RecoveryAction.POWER_CYCLE,
      RecoveryAction.CHECK_CONNECTION,
      RecoveryAction.CONTACT_SUPPORT,
    ];

    return manualActions.includes(primaryAction.action);
  }

  // ============================================================================
  // Recovery Suggestions
  // ============================================================================

  /**
   * Suggest recovery actions based on error code
   */
  suggestRecoveryActions(errorCode: ErrorCode, step: WizardStep): RecoveryInstruction[] {
    // Return default recovery actions for common errors
    switch (errorCode) {
      case ErrorCode.DEVICE_DISCONNECTED:
        return [
          {
            action: RecoveryAction.CHECK_CONNECTION,
            label: 'Reconnect Device',
            description: 'Check USB cable and reconnect the device',
            order: 1,
          },
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Attempt the operation again',
            order: 2,
          },
        ];

      case ErrorCode.TIMEOUT:
        return [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Try the operation again (may take longer)',
            order: 1,
          },
          {
            action: RecoveryAction.POWER_CYCLE,
            label: 'Power Cycle Device',
            description: 'Unplug device, wait 10 seconds, then replug',
            order: 2,
          },
        ];

      case ErrorCode.BOOTLOADER_TRANSFER_FAILED:
      case ErrorCode.EASY_INSTALLER_TRANSFER_FAILED:
      case ErrorCode.FIRMWARE_FLASH_FAILED:
        return [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Attempt the transfer again',
            order: 1,
          },
          {
            action: RecoveryAction.CHECK_CONNECTION,
            label: 'Check Connection',
            description: 'Ensure device is properly connected',
            order: 2,
          },
          {
            action: RecoveryAction.START_OVER,
            label: 'Start Over',
            description: 'Restart the entire update process',
            order: 3,
          },
        ];

      case ErrorCode.UNKNOWN_ERROR:
        return [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Try the operation again',
            order: 1,
          },
          {
            action: RecoveryAction.START_OVER,
            label: 'Start Over',
            description: 'Restart from the beginning',
            order: 2,
          },
          {
            action: RecoveryAction.CONTACT_SUPPORT,
            label: 'Contact Support',
            description: 'Get help from technical support',
            order: 3,
          },
        ];

      default:
        return [
          {
            action: RecoveryAction.RETRY_STEP,
            label: 'Retry',
            description: 'Attempt the operation again',
            order: 1,
          },
        ];
    }
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Get retry configuration for error code
   */
  private getRetryConfig(
    errorCode: ErrorCode,
    customConfig?: Partial<RetryConfig>
  ): RetryConfig {
    const defaultConfig =
      this.DEFAULT_RETRY_CONFIGS.get(errorCode) ||
      {
        maxAttempts: 1,
        initialDelay: 1000,
        maxDelay: 5000,
        backoffMultiplier: 2,
      };

    return { ...defaultConfig, ...customConfig };
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateBackoffDelay(
    attempt: number,
    initialDelay: number,
    maxDelay: number,
    multiplier: number
  ): number {
    const delay = initialDelay * Math.pow(multiplier, attempt - 1);
    return Math.min(delay, maxDelay);
  }

  /**
   * Sleep helper
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Create recovery instructions helper
 */
export function createRecoveryInstruction(
  action: RecoveryAction,
  label: string,
  description: string,
  order: number = 1
): RecoveryInstruction {
  return { action, label, description, order };
}

/**
 * Create UpdaterError with recovery actions helper
 */
export function createUpdaterError(
  message: string,
  code: ErrorCode,
  step: WizardStep,
  recoveryActions: RecoveryInstruction[],
  technicalDetails?: string
): UpdaterError {
  return new UpdaterError(message, code, step, recoveryActions, technicalDetails);
}
